import type { DragEvent } from "react";
import type {
  AssetResponse,
  ProjectSummary,
  SessionSummary,
  SlimSessionRef,
} from "../../generated/api/types.gen";
import type { ProjectWithDesignCount } from "./GalleryFolderList";

export const GALLERY_SESSION_DRAG_TYPE = "application/x-iqdesign-session-id";
/**
 * Marker stamped on the drag event when the session being dragged isn't
 * owned by the current user — i.e. they're using org-share access. Drop
 * targets outside the org tree read this and reject the drop.
 */
export const GALLERY_SESSION_REQUIRES_ORG_DRAG_TYPE =
  "application/x-iqdesign-requires-org";

/**
 * MIME type stamped on the drag event when a personal asset card is dragged.
 * Only the org assets section accepts drops of this type; the personal
 * section ignores it. Org-shared cards do not start a drag at all (the
 * ticket rule says you cannot drag assets *out* of the org folder).
 */
export const GALLERY_ASSET_DRAG_TYPE = "application/x-iqdesign-asset-id";

/** Stamp a "move" drag with an id under both the given MIME type and text/plain. */
function stampDrag(
  event: DragEvent<HTMLElement>,
  mimeType: string,
  id: string,
) {
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData(mimeType, id);
  event.dataTransfer.setData("text/plain", id);
}

/** Stamp a drag event with the session id so any drop target can read it back. */
export function startSessionDrag(
  session: SlimSessionRef | SessionSummary,
  event: DragEvent<HTMLElement>,
) {
  stampDrag(event, GALLERY_SESSION_DRAG_TYPE, session.session_id);
  if (!session.is_owned_by_current_user) {
    event.dataTransfer.setData(GALLERY_SESSION_REQUIRES_ORG_DRAG_TYPE, "1");
  }
}

/** Stamp a drag event with the asset id (personal assets only). */
export function startAssetDrag(
  asset: AssetResponse,
  event: DragEvent<HTMLElement>,
) {
  stampDrag(event, GALLERY_ASSET_DRAG_TYPE, asset.id);
}

export type AssetScope = "personal" | "organization";

export type SelectedGalleryNode =
  | { type: "root" }
  | { type: "project"; projectId: string }
  | { type: "assets"; scope: AssetScope };

export interface GalleryTreeData {
  projectsByParentId: Map<string | null, ProjectSummary[]>;
  sessionsByProjectId: Map<string | null, SlimSessionRef[]>;
  /** Active org's auto-created root folder, peeled out of the personal root group. */
  orgRoot: ProjectSummary | null;
}

function addToMap<T>(
  map: Map<string | null, T[]>,
  key: string | null,
  item: T,
) {
  const currentItems = map.get(key) ?? [];
  currentItems.push(item);
  map.set(key, currentItems);
}

export function buildGalleryTree(
  projects: ProjectSummary[],
  sessions: SlimSessionRef[],
): GalleryTreeData {
  const projectsByParentId = new Map<string | null, ProjectSummary[]>();
  const sessionsByProjectId = new Map<string | null, SlimSessionRef[]>();

  // The org root is the unique top-level project flagged as shared. It gets
  // its own drawer section, so we exclude it from the personal root group
  // (uniqueness is enforced server-side by uq_projects_org_root).
  const orgRoot =
    projects.find(
      (project) =>
        project.is_shared_with_organization && !project.parent_project_id,
    ) ?? null;

  for (const project of projects) {
    if (orgRoot && project.id === orgRoot.id) continue;
    addToMap(projectsByParentId, project.parent_project_id ?? null, project);
  }

  for (const session of sessions) {
    addToMap(sessionsByProjectId, session.project_id ?? null, session);
  }

  for (const projectGroup of projectsByParentId.values()) {
    projectGroup.sort((a, b) => a.name.localeCompare(b.name));
  }

  return { projectsByParentId, sessionsByProjectId, orgRoot };
}

export function enrichProjects(
  selectedNode: SelectedGalleryNode,
  treeData: GalleryTreeData,
): ProjectWithDesignCount[] {
  if (selectedNode.type === "assets") return [];
  const projectsForContentPanel =
    selectedNode.type === "root"
      ? (treeData.projectsByParentId.get(null) ?? [])
      : (treeData.projectsByParentId.get(selectedNode.projectId) ?? []);
  return projectsForContentPanel.map((p) => ({
    ...p,
    design_count: treeData.sessionsByProjectId.get(p.id)?.length ?? 0,
  }));
}

/**
 * Maps the gallery search params onto the selected drawer node. The org asset
 * scope is entitlement-gated: a bookmarked or stale
 * ``?view=assets&scope=organization`` link falls back to the personal scope
 * when the active org isn't paid or billing-exempt, so an ungated user never
 * lands on a permanently empty org grid.
 */
export function resolveSelectedNode(
  search: {
    projectId?: string;
    view?: "assets";
    scope?: AssetScope;
  },
  canUseOrgFeatures: boolean,
): SelectedGalleryNode {
  if (search.view === "assets") {
    const scope = search.scope ?? "personal";
    return {
      type: "assets",
      scope:
        scope === "organization" && !canUseOrgFeatures ? "personal" : scope,
    };
  }
  return search.projectId
    ? { type: "project", projectId: search.projectId }
    : { type: "root" };
}

/** Resolves the title from either a slim ref or a hydrated session summary. */
export function getDesignTitle(session: SlimSessionRef | SessionSummary) {
  if (session.name) return session.name;
  return "last_image" in session
    ? session.last_image.design_subject
    : session.design_subject;
}

/** Root → leaf folder names for breadcrumbs (excludes virtual root). */
export function buildProjectAncestorChain(
  leafProjectId: string | null | undefined,
  projects: ProjectSummary[],
): { id: string; name: string }[] {
  if (!leafProjectId) return [];
  const byId = new Map(projects.map((p) => [p.id, p]));
  const chain: { id: string; name: string }[] = [];
  let current: string | null | undefined = leafProjectId;
  while (current) {
    const project = byId.get(current);
    if (!project) break;
    chain.unshift({ id: project.id, name: project.name });
    current = project.parent_project_id ?? null;
  }
  return chain;
}
